import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EnterpriseFeaturesPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('integrations');

  const tabs = [
    { id: 'integrations', label: 'Integrations', icon: 'Link' },
    { id: 'automation', label: 'Automation', icon: 'Zap' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' },
    { id: 'enterprise', label: 'Enterprise', icon: 'Building' }
  ];

  const integrations = [
    {
      name: 'Slack',
      description: 'Get notifications and manage hiring directly from Slack',
      status: 'available',
      category: 'Communication',
      icon: 'MessageSquare'
    },
    {
      name: 'Microsoft Teams',
      description: 'Integrate with Teams for seamless collaboration',
      status: 'available',
      category: 'Communication',
      icon: 'Users'
    },
    {
      name: 'Google Workspace',
      description: 'Sync calendars and documents with Google Workspace',
      status: 'available',
      category: 'Productivity',
      icon: 'Calendar'
    },
    {
      name: 'Salesforce',
      description: 'Connect hiring data with your CRM',
      status: 'coming_soon',
      category: 'CRM',
      icon: 'Database'
    }
  ];

  const automationRules = [
    {
      name: 'Auto-screen Applications',
      description: 'Automatically screen applications based on requirements',
      enabled: true,
      triggers: 3,
      actions: 8
    },
    {
      name: 'Interview Scheduling',
      description: 'Auto-schedule interviews based on availability',
      enabled: true,
      triggers: 5,
      actions: 12
    },
    {
      name: 'Candidate Follow-up',
      description: 'Send automated follow-up messages to candidates',
      enabled: false,
      triggers: 2,
      actions: 4
    }
  ];

  const renderIntegrations = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrations.map((integration, index) => (
          <div key={index} className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Icon name={integration.icon} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{integration.name}</h3>
                  <span className="text-xs text-text-muted">{integration.category}</span>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                integration.status === 'available' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {integration.status === 'available' ? 'Available' : 'Coming Soon'}
              </span>
            </div>
            <p className="text-text-secondary text-sm mb-4">{integration.description}</p>
            <Button
              variant={integration.status === 'available' ? 'primary' : 'outline'}
              size="sm"
              disabled={integration.status !== 'available'}
              className="w-full"
            >
              {integration.status === 'available' ? 'Connect' : 'Notify Me'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Automation Rules</h3>
        <div className="space-y-4">
          {automationRules.map((rule, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium text-text-primary">{rule.name}</h4>
                <p className="text-text-secondary text-sm">{rule.description}</p>
                <div className="flex items-center space-x-4 mt-2 text-xs text-text-muted">
                  <span>{rule.triggers} triggers</span>
                  <span>{rule.actions} actions</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-sm ${rule.enabled ? 'text-green-600' : 'text-text-muted'}`}>
                  {rule.enabled ? 'Active' : 'Inactive'}
                </span>
                <button
                  className={`w-12 h-6 rounded-full transition-colors ${
                    rule.enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    rule.enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`}></div>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Compliance & Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Icon name="Shield" size={20} className="text-green-600" />
              <span className="font-medium">SOC 2 Type II Certified</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Lock" size={20} className="text-green-600" />
              <span className="font-medium">GDPR Compliant</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="FileText" size={20} className="text-green-600" />
              <span className="font-medium">CCPA Compliant</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Icon name="Key" size={20} className="text-green-600" />
              <span className="font-medium">256-bit SSL Encryption</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Database" size={20} className="text-green-600" />
              <span className="font-medium">Data Backup & Recovery</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="UserCheck" size={20} className="text-green-600" />
              <span className="font-medium">Background Check Integration</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEnterprise = () => (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Enterprise Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-primary-50 rounded-lg">
            <Icon name="Users" size={32} className="text-primary mx-auto mb-3" />
            <h4 className="font-semibold text-text-primary mb-2">Multi-tenant Architecture</h4>
            <p className="text-text-secondary text-sm">Separate data and workflows for different departments</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Icon name="BarChart3" size={32} className="text-blue-600 mx-auto mb-3" />
            <h4 className="font-semibold text-text-primary mb-2">Advanced Analytics</h4>
            <p className="text-text-secondary text-sm">Custom dashboards and reporting for executives</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Icon name="Headphones" size={32} className="text-green-600 mx-auto mb-3" />
            <h4 className="font-semibold text-text-primary mb-2">Dedicated Support</h4>
            <p className="text-text-secondary text-sm">24/7 priority support with dedicated account manager</p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6 border border-primary-200">
        <div className="text-center">
          <h3 className="text-xl font-bold text-text-primary mb-2">Ready to Scale?</h3>
          <p className="text-text-secondary mb-4">Contact our enterprise team for custom solutions</p>
          <Button variant="primary" iconName="Phone">
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'integrations': return renderIntegrations();
      case 'automation': return renderAutomation();
      case 'compliance': return renderCompliance();
      case 'enterprise': return renderEnterprise();
      default: return renderIntegrations();
    }
  };

  return (
    <div className="min-h-screen bg-background">

      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Enterprise Features</h1>
                <p className="text-text-secondary">Advanced tools and integrations for enterprise-scale hiring</p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-border mb-6">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary bg-primary-50'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {renderTabContent()}
        </div>
      </main>
</div>
  );
};

export default EnterpriseFeaturesPage;


